import React from 'react';

import {MaxPathLengthOptionResult} from './requests/use-query-result-max-length-options';
import {useRequestQueryResultsSubgraph} from './requests/use-request-query-results-subgraph';

import {GraphNode, GraphType} from '../../../../../components/graph/interface';
import {StructuredGraph} from '../../../../../components/graph/cytoscape-graph/graph-logic/structured-graph';
import {useLayoutAndCameraProps} from './use-layout-and-camera-props';
import {GraphPresentation, GraphPresentationDetails} from './interface';
import {usePresentationEditedProps} from './use-presentation-edited-props';
import {useGraphSettings} from './use-graph-settings';
import {useUnsavedChangesActions} from './use-unsaved-changes-actions';
import {useHiddenNodes} from './use-hidden-nodes';
import {useHiddenNodesActions} from '../../../../../components/graph/cytoscape-graph/graph-logic/hidden-nodes-actions/use-hidden-nodes-actions';
import {useGraphSelectionProps} from '../../../../../components/graph/use-graph-selection-props';
import {GenestackGraphGrid} from '../../../../../components/graph/biokb-graph/genestack-graph-grid';
import {useActionHistory} from './use-action-history';
import {useQueryGraphActions} from './use-query-graph-actions';
import {useUndoRedo} from './use-undo-redo';
import {useOnNodesLoaded} from './use-on-nodes-loaded';
import {useResultsListProps} from '../result-chains-panel/use-results-list-props';

interface Props {
    selectedQuery: CompletedQuery;
    maxPathLengthOptions: MaxPathLengthOptionResult;
    selectedPresentation: GraphPresentation;
    selectedPresentationDetails: GraphPresentationDetails;
    selectionProps: ReturnType<typeof useGraphSelectionProps>;
    resultsListProps: ReturnType<typeof useResultsListProps>;
}

export function useQueryGraphProps({
    selectedQuery,
    maxPathLengthOptions,
    selectedPresentation,
    selectedPresentationDetails,
    selectionProps,
    resultsListProps
}: Props) {
    const actionHistoryProps = useActionHistory();

    const presentationEditedProps = usePresentationEditedProps({
        queryId: selectedQuery.id,
        presentation: selectedPresentation
    });

    const graphSettings = useGraphSettings({
        queryId: selectedQuery.id,
        maxPathLengthOptions,
        selectedPresentation,
        selectedPresentationDetails,
        handlePresentationEdited: presentationEditedProps.handlePresentationEdited
    });
    const {viewKind, maxPathLength, graphType} = graphSettings;

    const queryGraphRequest = useRequestQueryResultsSubgraph(
        viewKind,
        selectedQuery.id,
        maxPathLength
    );

    const structuredGraph = React.useMemo(() => {
        return queryGraphRequest.data ? new StructuredGraph(queryGraphRequest.data) : undefined;
    }, [queryGraphRequest.data]);

    const genestackGraphGrid = React.useMemo(() => {
        return structuredGraph && graphType === GraphType.biokb
            ? new GenestackGraphGrid(structuredGraph)
            : undefined;
    }, [structuredGraph, graphType]);

    const hiddenNodesProps = useHiddenNodes({
        queryId: selectedQuery.id,
        selectedPresentation,
        selectedPresentationDetails,
        handlePresentationEdited: presentationEditedProps.handlePresentationEdited,
        structuredGraph
    });

    const layoutAndCameraProps = useLayoutAndCameraProps({
        queryId: selectedQuery.id,
        maxPathLength: Number(maxPathLength),
        isDefaultMPLength: graphSettings.isDefaultMPLength,
        viewKind,
        isDefaultSelViewKind: graphSettings.isDefaultViewKind,
        nodes: queryGraphRequest.data?.nodes,
        edges: queryGraphRequest.data?.edges,
        selectedPresentation,
        selectedPresentationDetails,
        handlePresentationEdited: presentationEditedProps.handlePresentationEdited,
        isPresentationEdited: presentationEditedProps.isPresentationEdited,
        isHiddenNodesVisible: hiddenNodesProps.isHiddenNodesVisible,
        isHiddenNode: hiddenNodesProps.isHiddenNode,
        isHiddenEdge: hiddenNodesProps.isHiddenEdge,
        addToHistory: actionHistoryProps.addToHistory
    });

    const hiddenNodesActions = useHiddenNodesActions({
        hiddenNodes: hiddenNodesProps.hiddenNodes,
        selectedNodes: selectionProps.selectedNodes,
        setSelectedNodes: selectionProps.setSelectedNodes,
        setHiddenNodes: hiddenNodesProps.setHiddenNodes,
        structuredGraph,
        isHiddenNodesVisible: hiddenNodesProps.isHiddenNodesVisible,
        toggleIsHiddenNodesVisible: hiddenNodesProps.toggleIsHiddenNodesVisible,
        visibleNodesPositions: layoutAndCameraProps.visibleNodesPositions,
        invisibleNodesPositions: layoutAndCameraProps.invisbleNodesPositions,
        setVisibleNodesPositions: layoutAndCameraProps.setVisibleNodesPositions,
        setInvisibleNodesPositions: layoutAndCameraProps.setInvisibleNodesPositions,
        actionHistoryProps,
        presentationEditedProps
    });

    const {clearUnsavedChanges, setUnsavedChanges} = useUnsavedChangesActions({
        presentationEditedProps,
        graphSettings,
        layoutAndCameraProps,
        hiddenNodesProps,
        structuredGraph,
        actionHistoryProps
    });

    const graphActions = useQueryGraphActions({
        layoutAndCameraProps,
        actionHistoryProps,
        presentationEditedProps,
        graphSettings,
        maxPathLengthOptions,
        selectionProps,
        resultsListProps
    });

    const {canFetchNodeStructure} = useOnNodesLoaded({
        queryGraphRequest,
        onLoaded: (nodes: GraphNode[], previousLoadedNodes?: GraphNode[]) => {
            const newSelection = selectionProps.onNodesLoaded(nodes);
            if (newSelection) {
                graphActions.selectionChangedEffect(newSelection);
            }
            layoutAndCameraProps.onNodesLoaded(nodes, previousLoadedNodes);
        }
    });

    const undoRedoProps = useUndoRedo({
        actionHistoryProps,
        layoutAndCameraProps,
        graphActions,
        presentationEditedProps,
        graphSettings,
        selectionProps,
        hiddenNodesActionsProps: hiddenNodesActions,
        resultsListProps
    });

    return {
        queryGraphRequest,
        structuredGraph,
        genestackGraphGrid,
        layoutAndCameraProps,
        graphSettings,
        presentationEditedProps,
        clearUnsavedChanges,
        setUnsavedChanges,
        hiddenNodesProps,
        hiddenNodesActions,
        actionHistoryProps,
        graphActions,
        undoRedoProps,
        canFetchNodeStructure
    };
}
